import { useState } from "react";
import { useWatch } from "react-hook-form";

import styles from "./index.module.scss";
import CommentGroup from "../../../ADP/CommentGroup";
import TextInput from "../../../TextInput";
import RadioGroup from "../../../RadioGroup";
import PageMarker from "../../../ADP/PageMarker";
import NotificationBanner from "../../../NotificationBanner";
import Button from "../../../Button";
import { Text } from "../../../Type";
import Tooltip from "../../../Tooltip";
import Modal from "../../../../components/Modal";
import UploadDocumentsModal from "../../../../components/ADP/UploadDocumentsModal";
import {
  dateNotInFutureFieldOptions,
  nonSpecialCharacterOptions,
  phoneExtensionFieldOptions,
  phoneNumberFieldOptions,
  postalCodeFieldOptions,
  shortProvinceOptions,
} from "../../../../utils/forms/validation";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";

const Section3 = () => {
  const { clientFile, formMode: mode } = useClientFileContext();
  const isAgent = useWatch({ name: "section3.applicant-or-agent" }) === "agent";
  const inputStreetName = useWatch({ name: "section3.street-name" });
  const lotRequired = !inputStreetName;
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);

  const readOnly = (["locked", "view"].includes(mode) || clientFile.status === "PendingSignature");

  return (
    <>
      <PageMarker page={11} total={13} />
      <NotificationBanner
        className={styles.marginAfterPM}
        title="This section of the form may be signed only by the applicant or their agent."
        warning
      />
      <Text className={styles.margin}>
        I consent to the Ministry of Health (the Ministry) collecting the
        information I provide on this form for the purpose of assessing and
        verifying my eligibility to receive benefits under the Ministry’s
        Assistive Devices Program (the &quot;Program&quot;). In addition, I
        consent to the Ministry and the Workplace Safety and Insurance Board
        (WSIB) collecting, using and disclosing personal information about me,
        including the information on this form and information related to my
        entitlement to health care benefits under the Workplace Safety and
        Insurance Act (&quot;WSIA&quot;), for the purpose of assessing and
        verifying my eligibility to receive benefits under the Program and WSIA.
      </Text>
      <Text className={styles.margin}>
        The Ministry and WSIB will limit the information that they exchange
        about me to only that information that is necessary for the purpose
        above.
      </Text>
      <Text className={styles.margin}>
        The Ministry will only use and disclose my personal health information
        in accordance with the Personal Health Information Protection Act, 2004,
        and the Ministry&apos;s &quot;Statement of Information Practices&quot;
        which is accessible at: www.health.gov.on.ca. In addition, the WSIB will
        collect, use and disclose personal information about me from the
        Ministry for the purpose of administering and enforcing the WSIA.
      </Text>
      <Text className={styles.margin}>
        I understand that if I choose to withhold or withdraw my consent to the
        collection, use and disclosure of this information by the Ministry or
        WSIB, I may be denied coverage under the Program.
      </Text>
      <Text className={styles.margin}>
        For more information on the Ministry&apos;s Information Practices, or
        the collection, use or disclosure of the personal information on this
        form, call 1-800-268-6021/416-327-8804 or TTY: 416-327-4282 or write to
        the Program Manager, 5700 Yonge Street, 7th Floor, Toronto ON M2M 4K5.
      </Text>
      <div className={styles.line} />
      <Text className={styles.margin}>
        I have read the Applicant Information Sheet, understand the rules of
        eligibility for ADP and am eligible for the equipment specified. I
        certify that the information I have provided on this form is true,
        correct and complete to the best of my knowledge. I understand that this
        information is subject to audit.
      </Text>
      <Button
        disabled={readOnly}
        className={styles.margin}
        primary
        onClick={() => setUploadModalOpen(true)}
      >
        Upload Client Signature in Supporting Documents
      </Button>
      <Modal
        title="Supporting Documents"
        isOpen={isUploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
        }}
      >
        <UploadDocumentsModal
          clientFileId={clientFile.id}
          setUploadModalOpen={setUploadModalOpen}
        />
      </Modal>
      <CommentGroup
        section="section3"
        id="1-info"
        className={styles.paddingAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <div className={styles.grid50}>
          <TextInput
            section="section3"
            id="date"
            label="Date of Signature"
            placeholder="YYYY/MM/DD"
            requiredText="Please enter a valid date of signature"
            fieldOptions={{ ...dateNotInFutureFieldOptions, required: "This is a required field."}}
            readOnly={mode !== "edit"}
          />
        </div>
        <RadioGroup
          section="section3"
          name="applicant-or-agent"
          description="Signature from Applicant or Agent"
          options={[
            { label: "Applicant", value: "applicant" },
            { label: "Agent", value: "agent" },
          ]}
          horizontal
          requiredText="This is a required field."
          className={styles.verticalMargin}
          readOnly={mode !== "edit"}
        />
        {isAgent && (
          <RadioGroup
            section="section3"
            name="relationship"
            description="Relationship to applicant"
            options={[
              { label: "Spouse", value: "spouse" },
              { label: "Parent", value: "parent" },
              { label: "Legal Guardian", value: "guardian" },
              { label: "Public Trustee", value: "trustee" },
              { label: "Power of Attorney", value: "attorney" },
            ]}
            requiredText="This is a required field."
            className={styles.verticalMargin}
            readOnly={mode !== "edit"}
          />
        )}
        {isAgent && (
          <div className={styles.gridThirds}>
            <TextInput
              section="section3"
              id="last-name"
              label="Last Name"
              placeholder=""
              fieldOptions={{
                ...nonSpecialCharacterOptions,
                required: isAgent ? "This is a required field." : undefined,
              }}
              maxLength={40}
              readOnly={mode !== "edit"}
            />
            <TextInput
              section="section3"
              id="first-name"
              label="First Name"
              placeholder=""
              fieldOptions={{
                ...nonSpecialCharacterOptions,
                required: isAgent ? "This is a required field." : undefined,
              }}
              maxLength={40}
              readOnly={mode !== "edit"}
            />
            <TextInput
              section="section3"
              id="middle-initial"
              label="Middle Initial"
              placeholder=""
              fieldOptions={nonSpecialCharacterOptions}
              maxLength={40}
              readOnly={mode !== "edit"}
            />
          </div>
        )}
      </CommentGroup>

      <CommentGroup
        section="section3"
        id="2-address"
        className={styles.paddingAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        {isAgent && (
          <div>
            <Text size="large" weight="bold">
              Address
              <span className={styles.addressTooltipWrapper}>
                <Tooltip
                  id="unit-tooltip"
                  content="Please enter a full street address -or- a Lot/Concession/Rural Route"
                />
              </span>
            </Text>
            <div className={styles.gridThirds}>
              <TextInput
                section="section3"
                id="unit-number"
                label="Unit Number"
                fieldOptions={nonSpecialCharacterOptions}
                maxLength={6}
                readOnly={mode !== "edit"}
              />
              <TextInput
                section="section3"
                id="street-number"
                label="Street Number"
                fieldOptions={nonSpecialCharacterOptions}
                maxLength={9}
                readOnly={mode !== "edit"}
              />
              <TextInput
                section="section3"
                id="street-name"
                label="Street Name"
                fieldOptions={nonSpecialCharacterOptions}
                maxLength={100}
                readOnly={mode !== "edit"}
              />
            </div>
            <TextInput
              section="section3"
              id="lot-concession-rural"
              label="Lot/Concession/Rural Route"
              fieldOptions={{
                ...nonSpecialCharacterOptions,
                required: lotRequired
                  ? "Please enter either a Street address (above) or a Lot/Concession/Rural Route."
                  : undefined,
              }}
              maxLength={100}
              readOnly={mode !== "edit"}
            />
            <div className={styles.grid401525}>
              <TextInput
                section="section3"
                id="city"
                label="City"
                fieldOptions={{
                  ...nonSpecialCharacterOptions,
                  required: "This is a required field.",
                }}
                maxLength={40}
                readOnly={mode !== "edit"}
              />
              <TextInput
                section="section3"
                id="province"
                label="Province"
                fieldOptions={{
                  ...shortProvinceOptions,
                  required: "This is a required field.",
                }}
                maxLength={2}
                readOnly={mode !== "edit"}
              />
              <TextInput
                section="section3"
                id="postal-code"
                label="Postal Code"
                fieldOptions={{
                  ...postalCodeFieldOptions,
                  required: "This is a required field.",
                }}
                maxLength={10}
                readOnly={mode !== "edit"}
              />
            </div>
            <div className={styles.grid401525}>
              <TextInput
                section="section3"
                id="primary-tel"
                label="Primary Telephone Number"
                placeholder="000-000-0000"
                fieldOptions={phoneNumberFieldOptions}
                readOnly={mode !== "edit"}
              />
            </div>
            <div className={styles.grid4025}>
              <TextInput
                section="section3"
                id="business-tel"
                label="Business Telephone Number"
                placeholder="000-000-0000"
                fieldOptions={phoneNumberFieldOptions}
                readOnly={mode !== "edit"}
              />
              <TextInput
                section="section3"
                id="extension"
                label="Extension"
                placeholder="000"
                fieldOptions={phoneExtensionFieldOptions}
                readOnly={mode !== "edit"}
              />
            </div>
          </div>
        )}
      </CommentGroup>
    </>
  );
};

export default Section3;
